import React from 'react';
import { Clock, DollarSign, Target, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { Panel, StatCard, EmptyState } from '../ui';
import { formatCurrency, formatTime, formatTrendLabel } from './utils';

const TrendText = ({ trend }) => {
  if (!trend) return null;
  const Icon =
    trend.direction === 'up'
      ? ArrowUpRight
      : trend.direction === 'down'
        ? ArrowDownRight
        : Minus;
  const color =
    trend.direction === 'up'
      ? 'text-green-600'
      : trend.direction === 'down'
        ? 'text-red-600'
        : 'text-secondary-500';
  return (
    <div className={`text-sm mt-1 flex items-center justify-center gap-1 ${color}`}>
      <Icon className="h-3 w-3" />
      {formatTrendLabel(trend.deltaPct)} vs last period
    </div>
  );
};

const FinancialReports = ({ kpis, projectMetrics = [], trends = {}, currencyCode = 'USD' }) => (
  <div className="space-y-6" data-testid="financial-dashboard">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div>
        <StatCard
          icon={DollarSign}
          tone="green"
          label="Total Revenue"
          value={formatCurrency(kpis.totalRevenue, currencyCode)}
        />
        <TrendText trend={trends.revenue} />
      </div>
      <div>
        <StatCard
          icon={Clock}
          tone="blue"
          label="Billable Hours"
          value={`${(kpis.billableHours || 0).toFixed(1)}h`}
        />
        <div className="text-sm text-blue-600 mt-1 text-center">
          {(kpis.billablePercentage || 0).toFixed(1)}% efficiency
        </div>
      </div>
      <StatCard
        icon={Target}
        tone="purple"
        label="Avg Rate/Hour"
        value={formatCurrency((kpis.totalRevenue || 0) / Math.max(1, kpis.billableHours || 0), currencyCode)}
      />
    </div>

    <Panel>
      <h3 className="text-lg font-semibold mb-4">Revenue by Project</h3>
      {projectMetrics.length === 0 ? (
        <EmptyState title="No financial data" description="Billable project activity will show here." />
      ) : (
        <div className="space-y-3">
          {projectMetrics.map((project) => (
            <div
              key={project.id}
              className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg"
            >
              <div>
                <h4 className="font-medium text-secondary-900">{project.name}</h4>
                <p className="text-sm text-secondary-600">
                  {formatTime(project.billableTime)} billable •{' '}
                  {(project.efficiency || 0).toFixed(1)}% efficiency
                </p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-green-600">
                  {formatCurrency(project.revenue, currencyCode)}
                </div>
                <div className="text-sm text-secondary-500">
                  {project.hourly_rate ? `$${project.hourly_rate}/hr` : 'No rate set'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  </div>
);

export default FinancialReports;
